import { css } from "@emotion/css";
import { ArrowExpandIcon, DuplicateIcon } from "@octopusdeploy/design-system-icons";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ActivityStatus, KubernetesResourceManifestDiffResource, KubernetesResourceManifestSummaryResource, KubernetesResourceStatus } from "@octopusdeploy/octopus-server-client";
import React, { useCallback, useEffect, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import { useStickyPageHeaderHeight } from "~/areas/tasks/components/Task/Kubernetes/Manifests/useStickyPageHeaderHeight";
import type { CodeComponent } from "~/components/Code/Code";
import type { DiffViewType } from "~/components/DiffViewer/DiffViewer";
import { DiffViewer } from "~/components/DiffViewer/DiffViewer";
import StringHelper from "~/utils/StringHelper/index";
import { ObjectStatusIcon } from "../ObjectStatusIcon";
import { CodeViewerWithLoading } from "../components/CodeViewerWithLoading/CodeViewerWithLoading";
import { ExpandableCodeViewer, type ExpandableCodeViewerIconButtonAction } from "../components/ExpandableCodeViewer/ExpandableCodeViewer";
import { ExpandableCodeViewerGroup } from "../components/ExpandableCodeViewer/ExpandableCodeViewerGroup";
import { FilterObjectCounter } from "../components/FilterObjectCounter";
import { createUnchangedUnifiedDiff } from "./createUnchangedUnifiedDiff";
export type ManifestListItem = {
    key: string;
    krm: KubernetesResourceManifestSummaryResource;
    resourceStatus: KubernetesResourceStatus | undefined;
    stepStatus: ActivityStatus | undefined;
};
export type ManifestDiffListItem = ManifestListItem & {
    diffResource: KubernetesResourceManifestDiffResource;
};
function isManifestDiffItem(item: ManifestListItem): item is ManifestDiffListItem {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (item as ManifestDiffListItem).diffResource !== undefined;
}
export type FilteredItemCounts = {
    total: number;
    filteredTotal: number;
};
const styles = {
    manifestHeader: css({
        color: themeTokens.color.text.primary,
        font: text.body.bold.medium,
    }),
    manifestSubtitle: css({
        color: themeTokens.color.text.primary,
        font: text.body.regular.xSmall,
    }),
    codeViewerContainer: css({
        marginTop: space[16],
        marginBottom: space[16],
    }),
    diffRenderFailure: css({
        font: text.body.regular.medium,
        color: themeTokens.color.text.danger,
        marginLeft: space[12],
    }),
};
const expandableCodeViewerAnimationOptions = { unmountOnExit: false };
type ManifestViewerListProps = {
    manifestItems: ManifestListItem[];
    itemCounts: FilteredItemCounts;
    selectedItemId: string | undefined;
    diffViewType: DiffViewType;
};
export const ManifestViewerList = (props: ManifestViewerListProps) => {
    const pageHeaderHeight = useStickyPageHeaderHeight();
    const isLargerThanIpad = useMediaQuery({ query: `(min-width: 811px)` });
    const manifestDivsRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const codeComponentRefs = useRef<Record<string, CodeComponent | null>>({});
    const manifestTitle = (krm: KubernetesResourceManifestSummaryResource) => (<div>
            <div className={styles.manifestHeader}>{krm.KubernetesObjectName}</div>
            <div className={styles.manifestSubtitle}>
                {krm.KubernetesResourceKind}
                {krm.KubernetesObjectNamespace ? ` in ${krm.KubernetesObjectNamespace}` : null}
            </div>
        </div>);
    const getActions: (id: string) => ExpandableCodeViewerIconButtonAction[] = useCallback((id: string) => [
        {
            icon: <DuplicateIcon size={20} color={themeTokens.color.icon.primary}/>,
            onClick: async () => await codeComponentRefs.current[id]?.copyToClipboard(),
            accessibleName: "Copy content to clipboard",
            tooltip: "Copy to clipboard",
        },
        {
            icon: <ArrowExpandIcon size={20} color={themeTokens.color.icon.primary}/>,
            onClick: async () => codeComponentRefs.current[id]?.showInFullScreen(),
            accessibleName: "Enter full screen",
            tooltip: "Enter full screen",
        },
    ], []);
    const renderDiffViewer = (diffResource: KubernetesResourceManifestDiffResource) => {
        const unifiedDiff = diffResource.DiffResult === "Unchanged" ? createUnchangedUnifiedDiff(diffResource.KubernetesResourceManifest.ManifestContent) : diffResource.UnifiedDiff;
        //if the unified diff is empty, render an error message
        if (StringHelper.isNullOrWhiteSpace(unifiedDiff)) {
            return <p className={styles.diffRenderFailure}>Failed to render diff</p>;
        }
        switch (diffResource.DiffResult) {
            case "Unchanged":
                return <DiffViewer originalText={diffResource.KubernetesResourceManifest.ManifestContent} updatedText={diffResource.KubernetesResourceManifest.ManifestContent} unifiedDiff={unifiedDiff} diffType={props.diffViewType}/>;
            case "Added":
                return <DiffViewer originalText={""} updatedText={diffResource.KubernetesResourceManifest.ManifestContent} unifiedDiff={unifiedDiff} diffType={props.diffViewType}/>;
            case "Removed":
                return <DiffViewer originalText={diffResource.KubernetesResourceManifest.ManifestContent} updatedText={""} unifiedDiff={unifiedDiff} diffType={props.diffViewType}/>;
            case "Changed":
                return <DiffViewer originalText={diffResource.FromKubernetesResourceManifest.ManifestContent} updatedText={diffResource.ToKubernetesResourceManifest.ManifestContent} unifiedDiff={unifiedDiff} diffType={props.diffViewType}/>;
        }
    };
    useEffect(() => {
        if (props.selectedItemId) {
            const ref = manifestDivsRefs.current[props.selectedItemId];
            if (!ref) {
                return;
            }
            //the scroll-margin is used as a sneaky way to offset the scrollIntoView
            ref.style.scrollMarginTop = pageHeaderHeight;
            ref.scrollIntoView(true);
            ref.style.scrollMarginTop = "0";
        }
    }, [pageHeaderHeight, props.selectedItemId]);
    return (<ExpandableCodeViewerGroup title={<FilterObjectCounter total={props.itemCounts.total} filteredTotal={props.itemCounts.filteredTotal} objectLabel={"manifests"}/>} showGroupActions={true}>
            {props.manifestItems.map((item) => (<div key={item.key} ref={(ref) => (manifestDivsRefs.current[item.krm.Id] = ref)} className={styles.codeViewerContainer}>
                    <ExpandableCodeViewer title={item.krm.KubernetesObjectName} description={`${item.krm.KubernetesResourceKind}${item.krm.KubernetesObjectNamespace ? ` in ${item.krm.KubernetesObjectNamespace}` : ""}`} animation={expandableCodeViewerAnimationOptions} statusIcon={item.resourceStatus && <ObjectStatusIcon objectStatus={item.resourceStatus} stepStatus={item.stepStatus}/>} selected={item.krm.Id === props.selectedItemId} actions={isManifestDiffItem(item) ? undefined : getActions(item.krm.Id)} isExpandedByDefault={isManifestDiffItem(item) ? item.diffResource.DiffResult !== "Unchanged" : true}>
                        {isManifestDiffItem(item) ? (renderDiffViewer(item.diffResource)) : (<CodeViewerWithLoading ref={(ref) => (codeComponentRefs.current[item.krm.Id] = ref)} value={item.krm.ManifestContent.trimEnd()} language={"YAML"} showLineNumbers={true} lineWrapping={true} fullScreenDialogTitle={manifestTitle(item.krm)} isLoading={false} showIndentationMarkers={isLargerThanIpad}/>)}
                    </ExpandableCodeViewer>
                </div>))}
        </ExpandableCodeViewerGroup>);
};
